import { Result } from 'neverthrow';
import { FeatureFlagBool } from '../FeatureFlagBool';
import { FeatureFlagJson } from '../FeatureFlagJson';
import { FeatureFlagNumber } from '../FeatureFlagNumber';
import { FeatureFlagString } from '../FeatureFlagString';
import {
  FeatureFlagOverrideFailure,
  clearFeatureFlagOverride,
  clearFeatureFlagOverrides,
  readFeatureFlagOverrideStore,
  setFeatureFlagOverride,
} from './featureFlagOverrideStore';
import { FeatureFlagNames, FeatureFlagOverrideStore, SetFeatureFlagOverrideInput } from './featureFlagOverrideTypes';

type OverrideOptions = {
  reason?: string;
  ttlMinutes?: number;
};

/** Result returned by the dev-API setters: the next store on success, or a typed validation failure. */
type SetOverrideResult = Result<FeatureFlagOverrideStore, FeatureFlagOverrideFailure>;

/**
 * Agent/dev-facing `window.__modernloopDev.featureFlags` API over untrusted localStorage. The
 * `set*` methods return a {@link Result}: on invalid input they resolve to `err(...)` rather than
 * throwing, so callers should check `isOk()` before assuming the override was applied.
 */
export type ModernLoopFeatureFlagDevApi = {
  getStore: () => FeatureFlagOverrideStore;
  list: () => FeatureFlagOverrideStore['entries'];
  setBoolean: (key: FeatureFlagBool, value: boolean, options?: OverrideOptions) => SetOverrideResult;
  setJson: (
    key: FeatureFlagJson,
    value: SetFeatureFlagOverrideInput['value'],
    options?: OverrideOptions
  ) => SetOverrideResult;
  setNumber: (key: FeatureFlagNumber, value: number, options?: OverrideOptions) => SetOverrideResult;
  setString: (key: FeatureFlagString, value: string, options?: OverrideOptions) => SetOverrideResult;
  clear: (key: FeatureFlagNames) => FeatureFlagOverrideStore;
  clearAll: () => FeatureFlagOverrideStore;
};

declare global {
  interface Window {
    __modernloopDev?: {
      featureFlags?: ModernLoopFeatureFlagDevApi;
    };
  }
}

export const createFeatureFlagDevApi = (): ModernLoopFeatureFlagDevApi => {
  return {
    getStore: readFeatureFlagOverrideStore,
    list: () => readFeatureFlagOverrideStore().entries,
    setBoolean: (key, value, options = {}) =>
      setFeatureFlagOverride({ key, type: 'BOOLEAN', value, source: 'AGENT', ...options }),
    setJson: (key, value, options = {}) =>
      setFeatureFlagOverride({ key, type: 'JSON', value, source: 'AGENT', ...options }),
    setNumber: (key, value, options = {}) =>
      setFeatureFlagOverride({ key, type: 'NUMBER', value, source: 'AGENT', ...options }),
    setString: (key, value, options = {}) =>
      setFeatureFlagOverride({ key, type: 'STRING', value, source: 'AGENT', ...options }),
    clear: clearFeatureFlagOverride,
    clearAll: clearFeatureFlagOverrides,
  };
};

export const installFeatureFlagDevApi = () => {
  if (!globalThis.window) {
    return;
  }

  window.__modernloopDev = {
    ...window.__modernloopDev,
    featureFlags: createFeatureFlagDevApi(),
  };
};

installFeatureFlagDevApi();
